import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";
import { buildParams } from "../http-params.util";

/**
 * SettlementsApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/settlementsApi/settlementsApi.service.js.
 *
 * Legacy verbose `console.log` debug noise removed (it was just `::list`,
 * `::get`, etc).
 *
 * Two sub-namespaces:
 *   apiService.settlements.{list,get,create}
 *   apiService.settlements.transactions.{list,get}
 *
 * The legacy default pageNumber=1 quirk is preserved: when no page is
 * supplied, the URL gets `?pageNumber=1` rather than no querystring.
 */
const SETTLEMENTS_BASE = "/v1/api/settlements";

@Injectable({ providedIn: "root" })
export class SettlementsApiService {
  private readonly http = inject(HttpClient);

  list(page?: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SETTLEMENTS_BASE}/headers`, {
        params: buildParams({ pageNumber: page ?? 1 }),
      }),
    );
  }

  get(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${SETTLEMENTS_BASE}/${id}`));
  }

  create(date: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(SETTLEMENTS_BASE, null, {
        params: buildParams({ settlementEndDate: date }),
      }),
    );
  }

  readonly transactions = {
    list: (
      id: number | string,
      page?: number | string,
    ): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${SETTLEMENTS_BASE}/${id}/transactions`, {
          params: buildParams({ pageNumber: page ?? 1 }),
        }),
      ),

    // Note: legacy parameter order is (id, transId) but the URL slots them
    // as `/:transId/transactions/:id`. Preserved verbatim — looks like a
    // legacy bug but consumers depend on this argument order.
    get: (
      id: number | string,
      transId: number | string,
    ): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(
          `${SETTLEMENTS_BASE}/${transId}/transactions/${id}`,
        ),
      ),
  };
}
